import { useCallback } from 'react';
import { getBehaviorTracker, getRecommendationService } from '../utils/personalization';

interface UserLocation {
  latitude: number;
  longitude: number;
  city?: string;
  state?: string;
  formatted?: string;
}

export function usePersonalization() {
  const behaviorTracker = getBehaviorTracker();
  const recommendationService = getRecommendationService();

  // Track event view (with automatic browsing history)
  const trackEventView = useCallback(
    async (eventId: string, metadata?: Record<string, any>) => {
      return behaviorTracker.trackEventView(eventId, metadata);
    },
    [behaviorTracker],
  );

  // Track ticket clicks
  const trackTicketClick = useCallback(
    async (eventId: string, metadata?: Record<string, any>) => {
      return behaviorTracker.trackTicketClick(eventId, metadata);
    },
    [behaviorTracker],
  );

  // Track bookmark/save actions
  const trackBookmark = useCallback(
    async (eventId: string, metadata?: Record<string, any>) => {
      return behaviorTracker.trackBookmark(eventId, metadata);
    },
    [behaviorTracker],
  );

  // Track share actions
  const trackShare = useCallback(
    async (eventId: string, metadata?: Record<string, any>) => {
      return behaviorTracker.trackShare(eventId, metadata);
    },
    [behaviorTracker],
  );

  // Track search queries
  const trackSearch = useCallback(
    async (query?: string, metadata?: Record<string, any>) => {
      return behaviorTracker.trackSearch(query, metadata);
    },
    [behaviorTracker],
  );

  // Track filter usage
  const trackFilter = useCallback(
    async (filters: string[], metadata?: Record<string, any>) => {
      return behaviorTracker.trackFilter(filters, metadata);
    },
    [behaviorTracker],
  );

  // Track location searches
  const trackLocationSearch = useCallback(
    async (location: string, metadata?: Record<string, any>) => {
      return behaviorTracker.trackLocationSearch(location, metadata);
    },
    [behaviorTracker],
  );

  // Recommendation fetchers
  const getPersonalizedRecommendations = useCallback(
    async (userLocation?: UserLocation) => {
      return recommendationService.getForYouRecommendations({
        lat: userLocation?.latitude,
        lon: userLocation?.longitude,
        limit: 10,
      });
    },
    [recommendationService],
  );

  const getSimilarEvents = useCallback(
    async (eventId: string, limit: number = 5) => {
      return recommendationService.getSimilarEvents(eventId, limit);
    },
    [recommendationService],
  );

  const getNewArtistEvents = useCallback(
    async (userLocation?: UserLocation) => {
      return recommendationService.getNewArtistEvents({
        lat: userLocation?.latitude,
        lon: userLocation?.longitude,
        limit: 8,
      });
    },
    [recommendationService],
  );

  const getHappeningNowEvents = useCallback(
    async (userLocation?: UserLocation) => {
      return recommendationService.getHappeningNowEvents({
        lat: userLocation?.latitude,
        lon: userLocation?.longitude,
        limit: 6,
      });
    },
    [recommendationService],
  );

  const getSavedEvents = useCallback(async () => {
    return recommendationService.getSavedEvents();
  }, [recommendationService]);

  // Event management
  const saveEvent = useCallback(
    async (eventId: string) => {
      return recommendationService.saveEvent(eventId);
    },
    [recommendationService],
  );

  const unsaveEvent = useCallback(
    async (eventId: string, options?: { signal?: AbortSignal }) => {
      return recommendationService.unsaveEvent(eventId, options);
    },
    [recommendationService],
  );

  const unsaveEventBeacon = useCallback(
    (eventId: string) => {
      return recommendationService.unsaveEventBeacon(eventId);
    },
    [recommendationService],
  );

  // User session management
  const setUserId = useCallback(
    (userId: string) => {
      behaviorTracker.setUserId(userId);
    },
    [behaviorTracker],
  );

  const clearUserId = useCallback(() => {
    behaviorTracker.clearUserId();
  }, [behaviorTracker]);

  const getUserId = useCallback(() => {
    return behaviorTracker.getUserId();
  }, [behaviorTracker]);

  const getSessionId = useCallback(() => {
    return behaviorTracker.getSessionId();
  }, [behaviorTracker]);

  return {
    // Tracking functions
    trackEventView,
    trackTicketClick,
    trackBookmark,
    trackShare,
    trackSearch,
    trackFilter,
    trackLocationSearch,

    // Recommendation functions
    getPersonalizedRecommendations,
    getSimilarEvents,
    getNewArtistEvents,
    getHappeningNowEvents,
    getSavedEvents,

    // Event management
    saveEvent,
    unsaveEvent,
    unsaveEventBeacon,

    // User session
    setUserId,
    clearUserId,
    getUserId,
    getSessionId,

    // Direct access to services
    behaviorTracker,
    recommendationService,
  };
}

// Hook for tracking search behavior
export function useSearchTracking() {
  const behaviorTracker = getBehaviorTracker();

  const trackSearchQuery = useCallback(
    async (query: string, resultCount?: number) => {
      return behaviorTracker.trackSearch(query, {
        resultCount,
        timestamp: new Date().toISOString(),
      });
    },
    [behaviorTracker],
  );

  const trackSearchFilter = useCallback(
    async (filters: string[], resultCount?: number) => {
      return behaviorTracker.trackFilter(filters, {
        resultCount,
        timestamp: new Date().toISOString(),
      });
    },
    [behaviorTracker],
  );

  return {
    trackSearchQuery,
    trackSearchFilter,
  };
}

// Hook for tracking event interactions
export function useEventTracking() {
  const behaviorTracker = getBehaviorTracker();

  const trackEventInteraction = useCallback(
    async (
      eventId: string,
      action: 'view' | 'click_tickets' | 'bookmark' | 'share',
      metadata?: Record<string, any>,
    ) => {
      const commonMetadata = {
        timestamp: new Date().toISOString(),
        ...metadata,
      };

      switch (action) {
        case 'view':
          return behaviorTracker.trackEventView(eventId, commonMetadata);
        case 'click_tickets':
          return behaviorTracker.trackTicketClick(eventId, commonMetadata);
        case 'bookmark':
          return behaviorTracker.trackBookmark(eventId, commonMetadata);
        case 'share':
          return behaviorTracker.trackShare(eventId, commonMetadata);
        default:
          console.warn(`Unknown event action: ${action}`);
          return false;
      }
    },
    [behaviorTracker],
  );

  return {
    trackEventInteraction,
  };
}
